import { useYjsProvider } from '@liveblocks/react/_private'
import { useCallback, useSyncExternalStore } from 'react'

// True ONLY once the Liveblocks Yjs provider has finished its initial sync with
// the server — i.e. the collaborative document's content has actually arrived.
//
// This is the strictly-stronger sibling of `@liveblocks/react-tiptap`'s
// `useIsEditorReady`, which returns true for status "synchronizing" OR
// "synchronized" — so `useIsEditorReady` flips true while the initial content is
// still IN FLIGHT and the editor doc is still empty. Any doc-reading reconcile /
// validation effect gated on `isEditorReady` (or on nothing) can therefore read
// an empty/stale doc on a cold reload and mistake a committed accept for orphaned
// content. Gating on THIS signal ("synchronized" only) waits for the content to
// be present, closing that race. Mirrors the provider-subscription shape
// `useIsEditorReady` uses internally (useSyncExternalStore over the provider's
// "status" event).
export function useIsDocSynchronized(): boolean {
  const yjsProvider = useYjsProvider()
  const getSnapshot = useCallback(
    () => yjsProvider?.getStatus() === 'synchronized',
    [yjsProvider]
  )
  const subscribe = useCallback(
    (callback: () => void) => {
      if (yjsProvider === undefined) {
        return () => {}
      }
      yjsProvider.on('status', callback)
      return () => {
        yjsProvider.off('status', callback)
      }
    },
    [yjsProvider]
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
